import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerApi } from '../api/endpoints.js';
import { useApi } from '../hooks/useApi.js';
import PageHeader from '../components/PageHeader.jsx';
import DataTable from '../components/DataTable.jsx';
import Toolbar from '../components/Toolbar.jsx';
import Badge from '../components/Badge.jsx';
import { Select, TextInput } from '../components/Field.jsx';
import { formatCurrency, fullName } from '../utils/format.js';
import { CUSTOMER_STATUSES } from '../constants.js';
import ImportProspectsModal from '../components/ImportProspectsModal.jsx';

/**
 * One screen reused by the three "customer list" menu entries. The API decides
 * what each role actually receives, so no filtering happens here.
 */
const CustomerListPage = ({
  titleKey,
  subtitle,
  subtitleKey,
  showOwners = true,
  baseQuery = {},
  // Only the prospect list takes a file: that is where a bought list lands.
  importable = false,
  // ... and it is the only list where every row is something to call.
  callable = false,
  // Whether the deal has been signed, which is what releases the account to the
  // IT team. Shown where that question is being asked - the rep's own list and
  // the IT list - and left off the prospect list, where nothing has been sold.
  showSignature = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState({ q: '', status: '', signed: '', page: 1, pageSize: 25 });
  const [importing, setImporting] = useState(false);

  // `baseQuery` is imposed by the route (e.g. only companies never called).
  const effectiveQuery = { ...query, ...baseQuery };

  const { data, loading, refresh } = useApi(() => customerApi.list(effectiveQuery), [query]);

  const patch = (part) => setQuery((current) => ({ ...current, ...part, page: 1 }));

  const columns = [
    {
      key: 'companyName',
      header: t('customers.companyName'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.companyName}</span>
          <span className="cell-muted">
            {[row.city, row.country].filter(Boolean).join(', ') || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'contact',
      header: t('customers.contact'),
      render: (row) => (
        <div className="cell-stack">
          <span>{[row.contactFirstName, row.contactLastName].filter(Boolean).join(' ') || '-'}</span>
          <span className="cell-muted">{row.email || row.phone || '-'}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => <Badge value={row.status} label={t(`customers.status.${row.status}`)} />,
    },
    {
      key: 'estimatedValue',
      header: t('customers.estimatedValue'),
      className: 'cell-right',
      render: (row) => formatCurrency(row.estimatedValue, i18n.resolvedLanguage, row.currency),
    },
  ];

  if (callable) {
    columns.push({
      key: 'call',
      header: '',
      className: 'cell-right',
      render: (row) => (
        <button
          type="button"
          className="btn btn--small btn--primary"
          onClick={(event) => {
            // The row itself opens the file; this opens the call script.
            event.stopPropagation();
            navigate(`/telepro/qualification?customerId=${row.id}`);
          }}
        >
          {t('leads.call')}
        </button>
      ),
    });
  }

  if (showSignature) {
    columns.push({
      key: 'signed',
      header: t('customers.signed'),
      render: (row) => (
        <Badge
          value={row.signed ? 'SIGNED' : 'NOT_SIGNED'}
          label={row.signed ? t('common.yes') : t('common.no')}
        />
      ),
    });
  }

  if (showOwners) {
    columns.push(
      { key: 'telepro', header: t('customers.telepro'), render: (row) => fullName(row.telepro) },
      { key: 'commercial', header: t('customers.commercial'), render: (row) => fullName(row.commercial) },
    );
  }

  return (
    <>
      <PageHeader
        title={t(titleKey)}
        subtitle={subtitleKey ? t(subtitleKey) : subtitle}
        actions={
          importable ? (
            <button type="button" className="btn btn--primary" onClick={() => setImporting(true)}>
              {t('import.action')}
            </button>
          ) : null
        }
      />

      <Toolbar>
        <TextInput
          value={query.q}
          onChange={(value) => patch({ q: value })}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
        />
        <Select
          value={query.status}
          onChange={(value) => patch({ status: value })}
          placeholder={t('common.all')}
          options={CUSTOMER_STATUSES.map((status) => ({ value: status, label: t(`customers.status.${status}`) }))}
        />
        {showSignature ? (
          <Select
            value={query.signed}
            onChange={(value) => patch({ signed: value })}
            placeholder={t('customers.anySignature')}
            options={[
              { value: 'yes', label: t('customers.signedOnly') },
              { value: 'no', label: t('customers.notSigned') },
            ]}
          />
        ) : null}
        <span className="toolbar__count">
          {data?.pagination?.total ?? 0} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={loading}
        emptyTitle={t('customers.emptyOwn')}
        onRowClick={(row) => navigate(`/customers/${row.id}`)}
      />

      <ImportProspectsModal
        open={importing}
        onClose={() => setImporting(false)}
        onImported={() => {
          // Close and reload: the new prospects are in the table behind.
          setImporting(false);
          setQuery((current) => ({ ...current, page: 1 }));
          refresh();
        }}
      />
    </>
  );
};

export default CustomerListPage;
